import React, { useRef, useState } from 'react'
import styled, { css } from 'styled-components'

// Styles
import { shadows } from '../../styles/effects'

// Components
import { HoverText, IPointerPosition } from '../tag/JobTag'

interface IProps {
  label: string
  active?: boolean
  disabled?: boolean
  onTrigger: () => void
  children: React.ReactNode
}

// Single icon button for the markdown toolbar. Fires onTrigger on mousedown
// (preventing default) so the textarea keeps focus and selection while the mark
// is applied. Shows an anchored hover tooltip with its label, and reflects
// active / disabled (e.g. during preview) via styling.
const MarkdownToolbarButton = ({
  label,
  active,
  disabled,
  onTrigger,
  children,
}: IProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [tooltipPosition, setTooltipPosition] = useState<IPointerPosition>({})

  const showTooltip = () => {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (!rect) return
    setTooltipPosition({
      x: Math.round(rect.left + rect.width / 2 + window.scrollX),
      y: Math.round(rect.bottom + window.scrollY),
    })
  }

  return (
    <StyledButton
      ref={buttonRef}
      type="button"
      aria-label={label}
      aria-pressed={active}
      active={active}
      disabled={disabled}
      // Use onMouseDown + preventDefault so the textarea keeps focus and its
      // selection while the formatting is applied.
      onMouseDown={(e) => {
        e.preventDefault()
        if (!disabled) onTrigger()
      }}
      onPointerEnter={showTooltip}
      onPointerLeave={() => setTooltipPosition({})}
    >
      {children}
      {tooltipPosition.x && tooltipPosition.y && (
        <HoverText text={label} position={tooltipPosition} anchored />
      )}
    </StyledButton>
  )
}

const StyledButton = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.textLightest};
  background-color: ${({ theme }) => theme.colors.lightest};
  box-shadow: ${shadows.depthOne};
  color: ${({ theme, active }) =>
    active ? theme.colors.textDark : theme.colors.text72};
  font-size: 14px;
  line-height: 16px;
  transition: background-color 0.2s ease, border-color 0.2s ease,
    color 0.2s ease;

  svg path {
    fill: currentColor;
  }

  ${({ active, theme }) =>
    active &&
    css`
      border-color: ${theme.colors.primary};
    `}

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.primaryFadeLight};
    border-color: transparent;
    color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`

export default MarkdownToolbarButton
